'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, Loader2, CheckCircle2, ArrowLeft, Mail } from 'lucide-react';

type Tab = 'login' | 'register';

interface LoginForm {
  email: string;
  password: string;
}

interface RegisterForm {
  displayName: string;
  email: string;
  password: string;
  confirmPassword: string;
  instagram: string;
  wechat: string;
}

export default function AuthClient() {
  const { t, language, toggleLanguage } = useLanguage();
  const { signIn, signUp, signInWithGoogle, signInWithApple } = useAuth();
  const router = useRouter();
  const [tab, setTab] = useState<Tab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | 'apple' | null>(null);
  const [success, setSuccess] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [authError, setAuthError] = useState('');

  const [loginForm, setLoginForm] = useState<LoginForm>({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState<RegisterForm>({
    displayName: '', email: '', password: '', confirmPassword: '', instagram: '', wechat: '',
  });

  const validateLogin = () => {
    const errs: Record<string, string> = {};
    if (!loginForm.email) errs.email = t('Email is required', '邮箱为必填项');
    else if (!/\S+@\S+\.\S+/.test(loginForm.email)) errs.email = t('Invalid email', '邮箱格式无效');
    if (!loginForm.password) errs.password = t('Password is required', '密码为必填项');
    return errs;
  };

  const validateRegister = () => {
    const errs: Record<string, string> = {};
    if (!registerForm.displayName.trim()) errs.displayName = t('Name is required', '姓名为必填项');
    if (!registerForm.email) errs.email = t('Email is required', '邮箱为必填项');
    else if (!/\S+@\S+\.\S+/.test(registerForm.email)) errs.email = t('Invalid email', '邮箱格式无效');
    if (!registerForm.password) errs.password = t('Password is required', '密码为必填项');
    else if (registerForm.password.length < 8) errs.password = t('Minimum 8 characters', '最少8个字符');
    if (registerForm.password !== registerForm.confirmPassword)
      errs.confirmPassword = t('Passwords do not match', '密码不匹配');
    return errs;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateLogin();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({}); setAuthError(''); setLoading(true);
    try {
      await signIn(loginForm.email, loginForm.password);
      setSuccess(true);
      setTimeout(() => router.push('/weekly-plan'), 800);
    } catch (err: any) {
      setAuthError(err?.message || t('Login failed. Please try again.', '登录失败，请重试。'));
    } finally { setLoading(false); }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateRegister();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({}); setAuthError(''); setLoading(true);
    try {
      await signUp(registerForm.email, registerForm.password, { displayName: registerForm.displayName, preferredLanguage: language });
      setPendingConfirmation(true);
    } catch (err: any) {
      setAuthError(err?.message || t('Registration failed. Please try again.', '注册失败，请重试。'));
    } finally { setLoading(false); }
  };

  const handleGoogleSignIn = async () => {
    setOauthLoading('google'); setAuthError('');
    try { await signInWithGoogle(); }
    catch (err: any) { setAuthError(err?.message || t('Google sign-in failed.', 'Google登录失败。')); setOauthLoading(null); }
  };

  const handleAppleSignIn = async () => {
    setOauthLoading('apple'); setAuthError('');
    try { await signInWithApple(); }
    catch (err: any) { setAuthError(err?.message || t('Apple sign-in failed.', 'Apple登录失败。')); setOauthLoading(null); }
  };

  const monoLabel: React.CSSProperties = {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  };

  if (pendingConfirmation) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F7F7F2', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 380 }}>
          <div style={{ width: 64, height: 64, borderRadius: 2, backgroundColor: '#B8FF00', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <Mail size={28} color="#070707" />
          </div>
          <h2 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontSize: 22, fontWeight: 900, color: '#070707', marginBottom: 12, textTransform: 'uppercase' }}>
            {t('Check your email', '请查收邮件')}
          </h2>
          <p style={{ fontSize: 14, color: '#5F625F', marginBottom: 8, lineHeight: 1.65 }}>
            {t('Please check your email to confirm your account.', '请查收邮件以确认您的账户。')}
          </p>
          <p style={{ fontSize: 12, color: '#5F625F', marginBottom: 28, lineHeight: 1.65 }}>
            {t('Click the confirmation link in the email we sent you. Once confirmed, you can log in.', '点击我们发送给您的邮件中的确认链接。确认后，您可以登录。')}
          </p>
          <Link href="/" className="btn-primary" style={{ display: 'inline-flex' }}>
            {t('Back to Home', '返回首页')}
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F7F7F2', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 380 }}>
          <div style={{ width: 64, height: 64, borderRadius: 2, backgroundColor: '#B8FF00', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <CheckCircle2 size={28} color="#070707" />
          </div>
          <h2 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontSize: 22, fontWeight: 900, color: '#070707', marginBottom: 12, textTransform: 'uppercase' }}>
            {tab === 'login' ? t('Welcome back!', '欢迎回来！') : t('Welcome to R3SET!', '欢迎加入R3SET！')}
          </h2>
          <p style={{ fontSize: 14, color: '#5F625F', marginBottom: 28 }}>
            {tab === 'login' ? t("You're logged in. Ready to run?", '您已登录。准备好跑步了吗？') : t('Your account has been created.', '您的账户已创建。')}
          </p>
          <Link href="/weekly-plan" className="btn-primary" style={{ display: 'inline-flex' }}>
            {t('View Weekly Plan', '查看训练计划')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F7F7F2', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', maxWidth: 480, margin: '0 auto', width: '100%' }}>
        <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none', color: '#5F625F', fontSize: 12, fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          <ArrowLeft size={13} />
          {t('Back', '返回')}
        </Link>
        <button
          onClick={toggleLanguage}
          style={{ padding: '4px 10px', borderRadius: 2, border: '1.5px solid #D8D8CE', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'var(--font-mono, monospace)', backgroundColor: 'transparent', color: '#5F625F', cursor: 'pointer' }}
        >
          {language === 'EN' ? '中文' : 'EN'}
        </button>
      </div>

      {/* Auth card */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 24px 40px' }}>
        <div style={{ width: '100%', maxWidth: 440, backgroundColor: '#FFFFFF', border: '1px solid #D8D8CE', borderRadius: 2, padding: 32 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
            <AppLogo size={24} />
            <span style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 14, color: '#070707', textTransform: 'uppercase' }}>R3SET</span>
            <span style={{ fontSize: 13, fontWeight: 400, color: '#5F625F' }}>Running</span>
          </div>

          <h1 style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 22, color: '#070707', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
            {tab === 'login' ? t('Welcome back', '欢迎回来') : t('Create account', '创建账户')}
          </h1>
          <p style={{ fontSize: 13, color: '#5F625F', marginBottom: 24, fontFamily: 'var(--font-body, sans-serif)' }}>
            {tab === 'login' ? t('Account access for members.', '会员账户登录。') : t('Register to access your member area.', '注册以进入会员中心。')}
          </p>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #D8D8CE', marginBottom: 24 }}>
            {(['login', 'register'] as Tab[]).map((tabKey) => (
              <button
                key={`auth-tab-${tabKey}`}
                onClick={() => { setTab(tabKey); setErrors({}); setAuthError(''); }}
                style={{
                  paddingBottom: 10, marginRight: 24,
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  fontFamily: 'var(--font-mono, monospace)',
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  borderBottom: tab === tabKey ? '2px solid #070707' : '2px solid transparent',
                  color: tab === tabKey ? '#070707' : '#5F625F',
                  marginBottom: -1,
                  transition: 'all 0.15s ease',
                }}
              >
                {tabKey === 'login' ? t('Login', '登录') : t('Register', '注册')}
              </button>
            ))}
          </div>

          {/* Auth Error */}
          {authError && (
            <div style={{ marginBottom: 16, padding: '10px 12px', borderRadius: 2, backgroundColor: '#FFE8E4', border: '1px solid #FFBFB8', fontSize: 12, color: '#B02010', fontFamily: 'var(--font-body, sans-serif)' }}>
              {authError}
            </div>
          )}

          {/* OAuth Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            <button onClick={handleGoogleSignIn} disabled={!!oauthLoading}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 16px', borderRadius: 2, border: '1.5px solid #D8D8CE', backgroundColor: '#FFFFFF', fontSize: 13, fontWeight: 600, color: '#070707', cursor: 'pointer', transition: 'border-color 0.15s ease', opacity: oauthLoading ? 0.6 : 1 }}>
              {oauthLoading === 'google' ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              )}
              {t('Continue with Google', '使用Google登录')}
            </button>
            <button onClick={handleAppleSignIn} disabled={!!oauthLoading}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 16px', borderRadius: 2, border: '1.5px solid #D8D8CE', backgroundColor: '#FFFFFF', fontSize: 13, fontWeight: 600, color: '#070707', cursor: 'pointer', transition: 'border-color 0.15s ease', opacity: oauthLoading ? 0.6 : 1 }}>
              {oauthLoading === 'apple' ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              )}
              {t('Continue with Apple', '使用Apple登录')}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, backgroundColor: '#D8D8CE' }} />
            <span style={{ fontSize: 11, color: '#5F625F', fontFamily: 'var(--font-mono, monospace)' }}>{t('or', '或')}</span>
            <div style={{ flex: 1, height: 1, backgroundColor: '#D8D8CE' }} />
          </div>

          {/* Login Form */}
          {tab === 'login' && (
            <form onSubmit={handleLoginSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }} noValidate>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#070707', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('Email', '邮箱')}</label>
                <input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="you@example.com" className={`input-field ${errors.email ? 'border-red-400' : ''}`} />
                {errors.email && <p style={{ fontSize: 11, color: '#B02010', marginTop: 4 }}>{errors.email}</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#070707', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('Password', '密码')}</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" className={`input-field ${errors.password ? 'border-red-400' : ''}`} style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#5F625F' }}>
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.password && <p style={{ fontSize: 11, color: '#B02010', marginTop: 4 }}>{errors.password}</p>}
              </div>
              <button type="submit" disabled={loading} className="btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
                {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} />}
                {t('Login', '登录')}
              </button>
            </form>
          )}

          {/* Register Form */}
          {tab === 'register' && (
            <form onSubmit={handleRegisterSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }} noValidate>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#070707', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('Name', '姓名')} *</label>
                <input type="text" value={registerForm.displayName} onChange={(e) => setRegisterForm({ ...registerForm, displayName: e.target.value })} placeholder={t('Your name', '你的名字')} className={`input-field ${errors.displayName ? 'border-red-400' : ''}`} />
                {errors.displayName && <p style={{ fontSize: 11, color: '#B02010', marginTop: 4 }}>{errors.displayName}</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#070707', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('Email', '邮箱')} *</label>
                <input type="email" value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} placeholder="you@example.com" className={`input-field ${errors.email ? 'border-red-400' : ''}`} />
                {errors.email && <p style={{ fontSize: 11, color: '#B02010', marginTop: 4 }}>{errors.email}</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#070707', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('Password', '密码')} *</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} placeholder={t('Min. 8 characters', '最少8个字符')} className={`input-field ${errors.password ? 'border-red-400' : ''}`} style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#5F625F' }}>
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.password && <p style={{ fontSize: 11, color: '#B02010', marginTop: 4 }}>{errors.password}</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#070707', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('Confirm Password', '确认密码')} *</label>
                <div style={{ position: 'relative' }}>
                  <input type={showConfirmPassword ? 'text' : 'password'} value={registerForm.confirmPassword} onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })} placeholder="••••••••" className={`input-field ${errors.confirmPassword ? 'border-red-400' : ''}`} style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#5F625F' }}>
                    {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.confirmPassword && <p style={{ fontSize: 11, color: '#B02010', marginTop: 4 }}>{errors.confirmPassword}</p>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#070707', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Instagram</label>
                  <input type="text" value={registerForm.instagram} onChange={(e) => setRegisterForm({ ...registerForm, instagram: e.target.value })} placeholder="@handle" className="input-field" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#070707', marginBottom: 6, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('WeChat', '微信')}</label>
                  <input type="text" value={registerForm.wechat} onChange={(e) => setRegisterForm({ ...registerForm, wechat: e.target.value })} placeholder="WeChat ID" className="input-field" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
                {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} />}
                {t('Create Account', '创建账户')}
              </button>
            </form>
          )}

          <p style={{ fontSize: 11, color: '#5F625F', textAlign: 'center', marginTop: 20, lineHeight: 1.6, fontFamily: 'var(--font-body, sans-serif)' }}>
            {t('By continuing, you agree to our ', '继续即表示您同意我们的')}
            <Link href="/terms-of-use" style={{ color: '#070707', fontWeight: 600, textDecoration: 'none' }}>{t('Terms', '条款')}</Link>
            {t(' and ', '和')}
            <Link href="/privacy-policy" style={{ color: '#070707', fontWeight: 600, textDecoration: 'none' }}>{t('Privacy Policy', '隐私政策')}</Link>.
          </p>

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #D8D8CE', textAlign: 'center' }}>
            <p style={{ fontSize: 11, color: '#5F625F', fontFamily: 'var(--font-body, sans-serif)' }}>
              {t('Want to join the community? ', '想加入社群？')}
              <Link href="/apply" style={{ color: '#070707', fontWeight: 700, textDecoration: 'none' }}>
                {t('Submit an application.', '提交申请。')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}